/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import "./Onboarding.css";
import plugLogo from "../../../assets/component/images/plug-impact-withText.png";
import { LANGUAGE_LABELS } from "../../PlugLanding/data";

// Nonprofit · the questions asked straight after the welcome, one per screen.
//
// These are not onboarding's own questions. They are the questions "Our
// profile" asks, from the same catalogue, in the same wording, written to the
// same answers — this screen is a second way through them, for an
// organisation that has just arrived and has nothing on its profile yet.
//
// Everything here can be skipped. A question passed over is simply
// unanswered, which is a state the profile page already knows how to show and
// already reminds an organisation about; nothing is lost by leaving, and
// nothing has to be finished in one sitting.
class Onboarding extends Component {
  /* Onboarding replaces the whole page, header included, so the workspace's
     language control is out of reach on it -- the same gap the sign-in pane
     had, and closed the same way: drawn on the brand rail, in the corner the
     marketing nav and the sign-in panel both keep it in.

     Read and written through the view model (vm.language, vm.setLanguage)
     rather than AuthConsumer, for the reason Login.renderLanguage gives: this
     component renders without a provider in tests. Both are guarded, so a
     bare render is a control that does nothing rather than a crash. */
  renderLanguage(vm) {
    return (
      <div className="Marketplace-Onboarding-lang">
        <span className="Marketplace-Onboarding-langGlobe" />
        <select
          className="Marketplace-Onboarding-langSelect"
          data-testid="onboarding-language"
          aria-label={vm.m.loginLanguage}
          value={vm.language || "en"}
          onChange={(e) => vm.setLanguage && vm.setLanguage(e.target.value)}
        >
          {Object.keys(LANGUAGE_LABELS).map((code) => (
            <option key={code} value={code}>
              {LANGUAGE_LABELS[code]}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // The rail down the left: what the run is for, every question in it, and
  // which of them are answered. Pressing a question jumps to it, so the run
  // reads as a list you are working through rather than a tunnel.
  renderRail(vm, ob) {
    return (
      <div className="Marketplace-Onboarding-rail">
        <div className="Marketplace-Onboarding-railTop">
          {/* The real mark, treated as the sign-in panel treats it: this rail
              is the same blue gradient, so the logo is tinted white rather
              than sat on a chip. It replaces a rounded square and the word
              "plug" set in Helvetica, which was a stand-in for the artwork. */}
          <div className="Marketplace-Onboarding-brand">
            <img src={plugLogo} alt="Plug" className="Marketplace-Onboarding-logo" />
            <span className="Marketplace-Onboarding-beta">{vm.m.beta}</span>
          </div>
          {this.renderLanguage(vm)}
        </div>
        <h2 className="Marketplace-Onboarding-heroTitle">{ob.heroTitle}</h2>
        <p className="Marketplace-Onboarding-heroBody">{ob.heroBody}</p>
        <div className="Marketplace-Onboarding-steps">
          {ob.steps.map((step) => (
            <div
              className={
                "Marketplace-Onboarding-step" +
                (step.current ? " Marketplace-Onboarding-step--on" : "")
              }
              key={step.id}
              onClick={step.go}
            >
              <span
                className={
                  "Marketplace-Onboarding-dot" +
                  (step.done ? " Marketplace-Onboarding-dot--done" : "")
                }
              >
                {step.mark}
              </span>
              <span className="Marketplace-Onboarding-stepLabel">{step.label}</span>
            </div>
          ))}
        </div>
        <div className="Marketplace-Onboarding-foot">{ob.heroFoot}</div>
      </div>
    );
  }

  // A short answer: the one question here that is typed rather than picked.
  // Committed on blur, the way the profile page commits its own text.
  renderText(ob) {
    return (
      <input
        className="Marketplace-Onboarding-text"
        type="text"
        maxLength={120}
        value={ob.textValue}
        placeholder={ob.textPlaceholder}
        onChange={ob.onText}
        onBlur={ob.onTextBlur}
      />
    );
  }

  renderChips(ob) {
    return (
      <div className="Marketplace-Onboarding-chips">
        {ob.options.map((option) => (
          <div
            className={
              "Marketplace-Onboarding-chip" +
              (option.on ? " Marketplace-Onboarding-chip--on" : "")
            }
            key={option.id}
            role="checkbox"
            aria-checked={option.on}
            onClick={option.pick}
          >
            <span
              className={
                "Marketplace-Onboarding-tick" +
                (option.on ? " Marketplace-Onboarding-tick--on" : "")
              }
            >
              {option.on ? "✓" : ""}
            </span>
            <span>{option.label}</span>
          </div>
        ))}
      </div>
    );
  }

  /**
   * A long option list: the picks so far, then a field that opens a searchable
   * panel of the rest.
   *
   * Seventeen SDGs as a wall of chips is a list nobody reads to the end, which
   * is the whole reason the catalogue marks a question `search`.
   */
  renderSearch(ob) {
    return (
      <div className="Marketplace-Onboarding-search">
        {ob.selected.length > 0 && (
          <div className="Marketplace-Onboarding-picks">
            {ob.selected.map((option) => (
              <div
                className="Marketplace-Onboarding-pick"
                key={option.id}
                onClick={option.pick}
              >
                {option.label}
                <span className="Marketplace-Onboarding-pickX">&#10005;</span>
              </div>
            ))}
          </div>
        )}
        <div className="Marketplace-Onboarding-field" onClick={ob.toggleDrop}>
          <span className="Marketplace-Onboarding-fieldLabel">{ob.dropLabel}</span>
          <span className="Marketplace-Onboarding-caret">
            {ob.dropOpen ? "▲" : "▼"}
          </span>
        </div>
        {ob.dropOpen && (
          <div className="Marketplace-Onboarding-panel">
            <div className="Marketplace-Onboarding-panelSearch">
              <input
                className="Marketplace-Onboarding-query"
                type="text"
                value={ob.dropQuery}
                placeholder={ob.dropPlaceholder}
                onChange={ob.onDropQuery}
              />
            </div>
            <div className="Marketplace-Onboarding-rows">
              {ob.dropRows.map((option) => (
                <div
                  className={
                    "Marketplace-Onboarding-row" +
                    (option.on ? " Marketplace-Onboarding-row--on" : "")
                  }
                  key={option.id}
                  role="checkbox"
                  aria-checked={option.on}
                  onClick={option.pick}
                >
                  <span
                    className={
                      "Marketplace-Onboarding-tick" +
                      (option.on ? " Marketplace-Onboarding-tick--on" : "")
                    }
                  >
                    {option.on ? "✓" : ""}
                  </span>
                  <span>{option.label}</span>
                </div>
              ))}
              {ob.dropEmpty && (
                <div className="Marketplace-Onboarding-noRows">{ob.noMatches}</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  renderQuestion(ob) {
    return (
      <div className="Marketplace-Onboarding-body">
        <div className="Marketplace-Onboarding-badges">
          {ob.badges.map((badge) => (
            <span
              className={
                "Marketplace-Onboarding-badge" +
                (badge.lead ? " Marketplace-Onboarding-badge--req" : "")
              }
              key={badge.id}
            >
              {badge.label}
            </span>
          ))}
        </div>
        <h3 className="Marketplace-Onboarding-title">{ob.title}</h3>
        <p className="Marketplace-Onboarding-hint">{ob.hint}</p>

        {ob.isText && this.renderText(ob)}
        {ob.isChips && this.renderChips(ob)}
        {ob.isSearch && this.renderSearch(ob)}

        {/* Only once "Other" is picked: a box for the words it stands in for,
            kept against the same answer. */}
        {ob.otherOn && (
          <div className="Marketplace-Onboarding-other">
            <div className="Marketplace-Onboarding-otherLabel">{ob.otherLabel}</div>
            {/* Keyed to the question. Without it React reused this node
                between questions and kept the previous question's text, which
                the next blur then wrote into the wrong answer. */}
            <input
              key={ob.questionId}
              className="Marketplace-Onboarding-otherInput"
              type="text"
              maxLength={300}
              defaultValue={ob.otherValue}
              onBlur={ob.onOther}
            />
          </div>
        )}

        <div className="Marketplace-Onboarding-picked">{ob.pickedLabel}</div>

        <div className="Marketplace-Onboarding-actions">
          {/* Held rather than removed on the first question, so the row does
              not reflow the moment the run starts. */}
          <div
            className={
              "Marketplace-Onboarding-back" +
              (ob.canBack ? "" : " Marketplace-Onboarding-back--hidden")
            }
            onClick={ob.canBack ? ob.onBack : undefined}
          >
            {ob.backLabel}
          </div>
          <div className="Marketplace-Onboarding-next" onClick={ob.onNext}>
            {ob.nextLabel}
          </div>
          <div className="Marketplace-Onboarding-skip" onClick={ob.onSkip}>
            {ob.skipLabel}
          </div>
          <div className="Marketplace-Onboarding-later" onClick={ob.onSkipAll}>
            {ob.laterLabel}
          </div>
        </div>
      </div>
    );
  }

  // What is still unanswered, and a way straight back to each of it. Only on
  // a run that was walked to the end with something skipped -- a finished run
  // has nothing to list.
  renderRemaining(ob) {
    return (
      <div className="Marketplace-Onboarding-remaining">
        <div className="Marketplace-Onboarding-remainingLabel">
          {ob.doneRemainingLabel}
        </div>
        <div className="Marketplace-Onboarding-remainingRows">
          {ob.doneRemaining.map((row) => (
            <div
              className="Marketplace-Onboarding-remainingRow"
              key={row.id}
              onClick={row.go}
            >
              <span className="Marketplace-Onboarding-remainingMark">!</span>
              <span>{row.label}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderDone(ob) {
    const open = (ob.doneRemaining || []).length > 0;
    return (
      <div className="Marketplace-Onboarding-body">
        {/* A tick is a claim, so it is kept for a run that earned it. A run
            closed with questions still open gets the neutral mark instead --
            nothing here failed, but nothing here is finished either. */}
        <div
          className={
            "Marketplace-Onboarding-done" +
            (open ? " Marketplace-Onboarding-done--open" : "")
          }
        >
          {open ? "•••" : "✓"}
        </div>
        <h3 className="Marketplace-Onboarding-doneTitle">{ob.doneTitle}</h3>
        <p className="Marketplace-Onboarding-doneBody">{ob.doneBody}</p>
        {open && this.renderRemaining(ob)}
        <div className="Marketplace-Onboarding-nextList">
          {ob.doneNext.map((row) => (
            <div className="Marketplace-Onboarding-nextRow" key={row.id}>
              <span className="Marketplace-Onboarding-nextNum">{row.n}</span>
              <div>
                <div className="Marketplace-Onboarding-nextTitle">{row.title}</div>
                <div className="Marketplace-Onboarding-nextBody">{row.body}</div>
              </div>
            </div>
          ))}
        </div>
        <div className="Marketplace-Onboarding-actions">
          <div className="Marketplace-Onboarding-next" onClick={ob.onDone}>
            {ob.doneCta}
          </div>
          <div className="Marketplace-Onboarding-skip" onClick={ob.onRestart}>
            {ob.doneReview}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const vm = this.props.vm;
    const ob = vm.onboarding;
    return (
      <div className="Marketplace-Onboarding">
        <div className="Marketplace-Onboarding-card">
          {this.renderRail(vm, ob)}
          <div className="Marketplace-Onboarding-main">
            <div className="Marketplace-Onboarding-head">
              <span className="Marketplace-Onboarding-count">{ob.stepLabel}</span>
            </div>
            <div className="Marketplace-Onboarding-bar">
              <div
                className="Marketplace-Onboarding-barFill"
                style={{ width: ob.pct }}
              />
            </div>
            {/* The answers decide which questions read as done, so the run
                waits for them rather than opening on a rail of empty marks. */}
            {ob.loading ? (
              <div className="Marketplace-Onboarding-loading">{ob.loadingLabel}</div>
            ) : ob.error ? (
              <div className="Marketplace-Onboarding-loading">
                {vm.m.proofSectionError}
                <span
                  className="Marketplace-Onboarding-retry"
                  onClick={ob.retry}
                >
                  {vm.m.proofRetry}
                </span>
              </div>
            ) : ob.asking ? (
              this.renderQuestion(ob)
            ) : (
              this.renderDone(ob)
            )}
          </div>
        </div>
      </div>
    );
  }
}

Onboarding.propTypes = {
  vm: PropTypes.object.isRequired,
};

export default Onboarding;
